import { computed, inject, Injectable, signal } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { catchError, from, map, Observable, of, switchMap, tap } from "rxjs";

import { AppConfigService } from '@core/services/config.service';
import { PostDpopResponse } from '@core/models/logger';
import { environment } from "@env/environment";

@Injectable()
export class DpopService {
  //injections
  private readonly http = inject(HttpClient);
  private readonly appConfigService = inject(AppConfigService);
  //properties
  private cryptoKeyPair: CryptoKeyPair;
  private publicJWK: JsonWebKey;
  private currentHash: string;
  private dpopTokenExpiration = signal<string>(null);
  //public properties
  public readonly sessionUniqueId = signal<string>(null);
  public readonly dpopToken = signal<string>(null);
  public readonly isTokenExpired = computed(() => new Date(this.dpopTokenExpiration()).toISOString() < new Date().toISOString());

  bootstrap(): Observable<PostDpopResponse | null> {
    //create unique session id
    this.sessionUniqueId.set(`${this.appConfigService.code()}_${Date.now()}`);
    //initialize crypto key pair & public JWK
    return this.initializeKeys().pipe(
      switchMap((keys) => {
        this.cryptoKeyPair = keys.cryptoKeyPair;
        this.publicJWK = keys.publicJWK;

        return this.getHash().pipe(
          switchMap((hash) => {
            if (!hash) return of(null);
            this.currentHash = hash;
            return this.postDpop(keys.publicJWK, hash).pipe(
              tap((dpop) => {
                if (dpop) {
                  this.dpopToken.set(dpop.token);
                  this.dpopTokenExpiration.set(dpop.exp);
                }
              })
            );
          })
        );
      })
    );
  }

  generateDpopToken(httpMethod: string, requestUrl: string): Observable<string> {
    if (!this.publicJWK || !this.cryptoKeyPair) return of(null);
    const header = { alg: 'ES256', typ: 'dpop+jwt', jwk: this.publicJWK };
    const payload = {
      htm: httpMethod.toUpperCase(),
      htu: this.getAbsoluteUrl(requestUrl).toLowerCase(), 
      iat: Math.floor(Date.now() / 1000),
      jti: crypto.randomUUID()
    };
    const headerB64 = this.decodeToBase64(JSON.stringify(header));
    const payloadB64 = this.decodeToBase64(JSON.stringify(payload));
    const stringToSign = `${headerB64}.${payloadB64}`;
    const signatureAlgorithm: EcdsaParams = {
      name: 'ECDSA',
      hash: 'SHA-256',
    };

    return from(crypto.subtle.sign(signatureAlgorithm, this.cryptoKeyPair.privateKey, new TextEncoder().encode(stringToSign))).pipe(
      map((signature) => {
        const joseSignature = this.convertSignatureToJose(signature);
        return `${stringToSign}.${this.decodeToBase64(joseSignature)}`;
      })
    );
  }

  refreshToken(): Observable<PostDpopResponse | null> {
    return this.postDpop(this.publicJWK, this.currentHash);
  }

  private getHash(): Observable<string | null> {
    //try to load the hash from local server first
    return this.http.get<any>(`${environment.localServerUrl}/logger/hash`).pipe(
      map((response: any) => response?.status === 'ok' ? response.hash : null),
      catchError((error) => {
        console.error('Error loading device hash', error?.message);
        return of(null);
      }),
      switchMap((hash) => {
        //if hash not found locally, fetch from remote server
        if (!hash) {
          const deviceId = this.appConfigService.code();
          return this.http.post<any>(`${environment.loggerApiUrl}/hash`, { deviceId }).pipe(
            map((response: any) => response?.status === 'success' && response.data ? response.data.hash : null),
            catchError((error) => {
              console.error('Error loading hash from api:', error.message);
              return of(null);
            })
          );
        }
        return of(hash);
      }),
    );
  }

  private postDpop(publicJWK: JsonWebKey, apiHash: string): Observable<PostDpopResponse | null> {
    const headers = new HttpHeaders().set('X-Api-Key', apiHash);
    const dto = { jwk: publicJWK };
    return this.http.post<PostDpopResponse>(`${environment.loggerApiUrl}/bootstrap`, dto, { headers }).pipe(
      map((response: any) => response.status === 'success' ? response.data : null),
      catchError((error) => {
        console.error(`Error posting DPop: ${error}`);
        return of(null);
      })
    );
  }

  private initializeKeys(): Observable<{cryptoKeyPair: CryptoKeyPair, publicJWK: JsonWebKey}> {
    return this.generateCryptoKey().pipe(
      switchMap(keyPair => this.generatePublicJwk(keyPair.publicKey).pipe(
        tap((jwk) => {
          this.cryptoKeyPair = keyPair;
          this.publicJWK = jwk;
        }),
        map((jwk) => ({cryptoKeyPair: keyPair, publicJWK: jwk}))
      )),
    );
  }

  private generateCryptoKey(): Observable<CryptoKeyPair> {
    const algorithm: EcKeyGenParams = {
      name: "ECDSA",
      namedCurve: "P-256",
    };
    return from(crypto.subtle.generateKey(algorithm, true, ["sign"]));
  }

  private generatePublicJwk(publicKey: CryptoKey): Observable<JsonWebKey> {
    return from(crypto.subtle.exportKey("jwk", publicKey))
  }

  private getAbsoluteUrl(requestUrl: string): string {
    return `${environment.loggerApiUrl}${requestUrl}`;
  }

  private decodeToBase64(input: ArrayBuffer | Uint8Array | string): string {
    const bytes = typeof input === 'string' ? new TextEncoder().encode(input) : (input instanceof Uint8Array ? input : new Uint8Array(input));
    let str = '';
    for (let i = 0; i < bytes.length; i++) {
      str += String.fromCharCode(bytes[i]);
    }
    return btoa(str).replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'');
  }

  // Convert a ECDSA DER (WebCrypto) to JOSE (r||s, 64 bytes) format
  // Supports two cases:
  // 1) Signature already in JOSE (r||s) 64 bytes format → return as is
  // 2) Signature DER ASN.1 (SEQUENCE of 2 INTEGER) → parse and convert to 64 bytes
  private convertSignatureToJose(signature: ArrayBuffer): Uint8Array {
    const bytes = signature instanceof ArrayBuffer ? new Uint8Array(signature) : signature;
    if (bytes.length === 64 && bytes[0] !== 0x30) {
      return bytes;
    }
  
    let offset = 0;
    if (bytes[offset++] !== 0x30) throw new Error('Invalid DER seq');
  
    // DER length: can be short (<=127) or long (>=128)
    let seqLen = bytes[offset++];
    if (seqLen & 0x80) {
      const numLenBytes = seqLen & 0x7f;
      if (numLenBytes === 0 || numLenBytes > 2) throw new Error('Unsupported DER length');
      seqLen = 0;
      for (let i = 0; i < numLenBytes; i++) seqLen = (seqLen << 8) | bytes[offset++];
    }
  
    if (bytes[offset++] !== 0x02) throw new Error('Invalid DER int r');
    let rLen = bytes[offset++];
    if (rLen & 0x80) {
      const numLenBytes = rLen & 0x7f;
      rLen = 0;
      for (let i = 0; i < numLenBytes; i++) rLen = (rLen << 8) | bytes[offset++];
    }
    let r = bytes.slice(offset, offset + rLen);
    offset += rLen;
  
    if (bytes[offset++] !== 0x02) throw new Error('Invalid DER int s');
    let sLen = bytes[offset++];
    if (sLen & 0x80) {
      const numLenBytes = sLen & 0x7f;
      sLen = 0;
      for (let i = 0; i < numLenBytes; i++) sLen = (sLen << 8) | bytes[offset++];
    }
    let s = bytes.slice(offset, offset + sLen);
  
    const padLeftTo32 = (v: Uint8Array) => {
      while (v.length > 0 && v[0] === 0x00) v = v.slice(1);
      if (v.length > 32) throw new Error('Component too long');
      const out = new Uint8Array(32);
      out.set(v, 32 - v.length);
      return out;
    };
  
    const r32 = padLeftTo32(r);
    const s32 = padLeftTo32(s);
  
    const sig = new Uint8Array(64);
    sig.set(r32, 0);
    sig.set(s32, 32);
    return sig;
  }
}
